import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
} from '@mui/material';

const PRIMARY = '#154A8A';

const dialogPaperSx = {
  '& .MuiDialog-paper': {
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    p: 2,
  },
};

const titleSx = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: PRIMARY,
  pb: 1,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
};

const secondaryBtnSx = {
  borderColor: 'grey.400',
  color: 'grey.700',
  '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
};

const primaryBtnSx = {
  bgcolor: PRIMARY,
  '&:hover': { bgcolor: '#0d3a6a' },
  '&:disabled': { bgcolor: PRIMARY, opacity: 0.5 },
};

function capitalizeWords(text) {
  if (!text) return '';
  return text.replace(/\b\w/g, (c) => c.toUpperCase());
}

function idEq(a, b) {
  return a != null && b != null && (a === b || String(a) === String(b));
}

/**
 * DBA process status change — shared by underwriting, pending, complete, sales DBA lists.
 */
export default function DbaStatusChangeDialog({
  open,
  onClose,
  onConfirm,
  selectedDba,
  statusOptions = [],
  value,
  onChange,
  processingId,
  title = 'Change DBA Status',
  confirmLabel = 'Change Status',
  description,
}) {
  const selected =
    statusOptions.find((s) => idEq(s.id, value)) || null;
  const busy = processingId && selectedDba && idEq(processingId, selectedDba.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={dialogPaperSx}>
      <DialogTitle sx={titleSx}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <DialogContentText sx={{ mb: 2, color: 'grey.700' }}>
          {description ??
            `Update the status for ${selectedDba?.name || 'this DBA'}. Select a new status below.`}
        </DialogContentText>
        <Autocomplete
          options={statusOptions}
          getOptionLabel={(option) => capitalizeWords(option?.name || '')}
          value={selected}
          onChange={(_, newValue) => onChange(newValue ? newValue.id : '')}
          isOptionEqualToValue={(opt, val) => idEq(opt?.id, val?.id)}
          renderInput={(params) => (
            <TextField {...params} label="Change the DBA status to" fullWidth />
          )}
          sx={{ bgcolor: 'white', borderRadius: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="secondary" sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={!value || busy}
          startIcon={busy ? <CircularProgress size={20} /> : null}
          sx={primaryBtnSx}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
